import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { ColorProps, FontSizeProps, LineHeightProps } from 'styled-system';
import { color, display, themeGet } from 'styled-system';

import type { TextDecorationProps, WhiteSpaceProps } from 'common/theme/system-utilities';
import { textDecoration, whiteSpace } from 'common/theme/system-utilities';
import { useLocalization } from '../../providers/LocaleProvider';
import { useRouter } from '../../providers/RouterProvider';
import type { Filter } from '../../types/SortAndFilter';
import { getProductsListPath, getUrlWithoutQueryParams } from '../../utils/url';
import { ButtonBase } from '../Button';
import { LinkRegularLR1 } from '../Typography';

interface ClearAllFiltersButtonProps {
  filters: Filter[];
  withTextDecoration?: boolean;
  withBorder?: boolean;
  className?: string;
  onNavigate?: () => void;
  navigationMethod?: 'push' | 'replace';
}

const StyledButtonV2 = styled(ButtonBase)<
  ColorProps & LineHeightProps & WhiteSpaceProps & FontSizeProps & TextDecorationProps
>`
  ${color};
  ${display};
  ${whiteSpace};
  ${textDecoration};

  text-underline-offset: 2px;

  &:active,
  &:hover,
  &:focus {
    color: ${themeGet('colors.sys.neutral.text.active')};
  }
`;

const styledButtonStyles: ComponentProps<typeof StyledButtonV2> = {
  display: 'inline-flex',
  whiteSpace: 'nowrap',
  color: 'sys.neutral.text.default',
  textDecoration: 'underline',
};

export const createRangeMinParamName = (key: string) => `${key}.min`;
export const createRangeMaxParamName = (key: string) => `${key}.max`;
export const createOptionsParamValue = (optionsKeys: string[]) => optionsKeys.sort().join('|');

const getFilterKeysToDelete = (filters: Filter[]): string[] => {
  const keysToDelete: string[] = [];

  filters.forEach(filter => {
    if ('options' in filter) {
      if (filter.options.some(option => option.selected)) {
        keysToDelete.push(filter.key);
      }
    } else if (filter.selectedMax || filter.selectedMin) {
      keysToDelete.push(createRangeMinParamName(filter.key));
      keysToDelete.push(createRangeMaxParamName(filter.key));
    }
  });

  return keysToDelete;
};

const ClearAllFiltersButton = ({
  filters,
  withBorder,
  className,
  onNavigate,
  navigationMethod = 'push',
}: ClearAllFiltersButtonProps) => {
  const { t } = useLocalization();
  const router = useRouter();
  const filterKeysToDelete = getFilterKeysToDelete(filters);

  const onClearAllFiltersClick = () => {
    if (filterKeysToDelete.length === 0) {
      return;
    }

    const clearAllFiltersUrl = getUrlWithoutQueryParams(filterKeysToDelete);
    const path = getProductsListPath(clearAllFiltersUrl);
    const navigate = navigationMethod === 'replace' ? router.replace : router.push;
    onNavigate?.();
    navigate(path, { scroll: true });
  };

  const props = {
    onClick: onClearAllFiltersClick,
    'data-testid': 'clear-all-filters-button',
    withBorder,
    className,
  };

  if (filterKeysToDelete.length === 0) {
    return null;
  }

  return (
    <StyledButtonV2
      {...props}
      {...styledButtonStyles}
      aria-label={t('h24_clear_all_filters_accessibility')}
    >
      <LinkRegularLR1 as="span" color="inherit">
        {t('h24_clear_all_filters')}
      </LinkRegularLR1>
    </StyledButtonV2>
  );
};

export default ClearAllFiltersButton;
